<script setup lang="ts">
import type LxcTable from "@lxc/app-device-common/src/components/LxcTable.vue";
import type { ComputedRef, ModelRef, Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { useSideNavigation } from "~/core/composables/useSideNavigation";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { CloseEvent } from "~/core/models/CloseEvent.interface";
import type LxcError from "~/core/models/LxcError.class";
import type { QuickFilterGroup } from "~/core/models/QuickFilterGroup.interface";
import type { QuickFilterItem } from "~/core/models/QuickFilterItem.interface";
import { Filters } from "~/core/models/filters";
import type { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import type UserForm from "~/modules/user/components/UserForm.vue";
import { useFetchUsers } from "~/modules/user/composables/useFetchUsers";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import type { User } from "~/modules/user/models/User.interface";
import { UserActions } from "~/modules/user/models/UserActions.enum";
import { UserColumns } from "~/modules/user/models/UserColumns.enum";
import type { UserDetails } from "~/modules/user/models/UserDetails.interface";
import type { UserProfile } from "~/modules/user/models/UserProfile.interface";
import { UserSelectionQuickFilter } from "~/modules/user/models/UserSelectionQuickFilter.enum";
import { getProfileLabel } from "~/modules/user/utils/profileLabel";

const { t } = useI18n();

const { can } = useAcl();

const props = defineProps<{
  businessUnitCode?: number;
  organizationCode?: number;
  columns?: UserColumns[];
  organizationType?: OrganizationType;
  pathContext: string;
  useExternalIdp?: boolean;
  disableClickable?: boolean;
  useQueryParametersForPagination: boolean;
  showQuickFilterSelectedUsers?: boolean;
}>();

const showUserForm: ModelRef<boolean> = defineModel<boolean>("showUserForm", {
  required: true,
});

const currentSelectedUser: ModelRef<string | undefined> = defineModel<
  string | undefined
>("currentSelectedUser");

const selectedUsers: ModelRef<string[] | undefined> = defineModel<
  string[] | undefined
>("selectedUsers", {
  required: false,
});

const {
  isLoading,
  error,
  results,
  fetchData,
  filters,
  setFilter,
  onSearch,
  search,
  onSortChange,
} = useFetchUsers({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
  useQueryParametersForPagination: props.useQueryParametersForPagination,
});
const { navigateToPath, onClose } = useSideNavigation();

const tableRef = ref<InstanceType<typeof LxcTable>>();
const userFormRef: Ref<InstanceType<typeof UserForm> | undefined> = ref();
const currentUser: ComputedRef<UserDetails | undefined> = computed(
  () => userFormRef.value?.currentUser,
);
const allProfiles: ComputedRef<UserProfile[] | undefined> = computed(
  () => userFormRef.value?.profileResults,
);
const isLoadingGetCurrentUser: ComputedRef<boolean | undefined> = computed(
  () => userFormRef.value?.isLoadingGetCurrentUser,
);
const errorGetCurrentUser: ComputedRef<LxcError | undefined> = computed(
  () => userFormRef.value?.errorGetCurrentUser,
);

/**
 * Property used for quick filters
 */
const initialSelectedUsers = ref<string[]>([]);
const selectedUsersSelectionFilter: Ref<UserSelectionQuickFilter> = ref(
  UserSelectionQuickFilter.ALL_USERS,
);

const getGroupsLabel = (groups: GroupsUserV2[]): string[] => {
  return groups?.map((group) => group.name);
};

const searchQuery = computed({
  get() {
    return (filters.get(Filters.USER_FIRST_OR_LAST_NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.USER_FIRST_OR_LAST_NAME, searchQuery);
  },
});

const quickFilterGroups = computed(() => {
  const groups: QuickFilterGroup[] = [];

  const selectionFilters = [
    UserSelectionQuickFilter.ALL_USERS,
    UserSelectionQuickFilter.SELECTED_USERS,
    UserSelectionQuickFilter.UNSELECTED_USERS,
  ];
  groups.push({
    key: "selection",
    items: selectionFilters.map((filter) => ({
      key: filter,
      label: t(`user.filters.selectionQuickFilters.${filter}`),
      isActive: selectedUsersSelectionFilter.value === filter,
    })),
    handler: (item) => filterBySelection(item.key as UserSelectionQuickFilter),
  });

  return groups;
});

const computedUsers: ComputedRef<User[] | undefined> = computed(() => {
  let users: User[];
  if (results.value?.results) {
    if (
      props.showQuickFilterSelectedUsers &&
      selectedUsersSelectionFilter.value ===
        UserSelectionQuickFilter.SELECTED_USERS
    ) {
      users = results.value.results.filter((user: User) =>
        initialSelectedUsers.value?.includes(user.login),
      );
    } else if (
      props.showQuickFilterSelectedUsers &&
      selectedUsersSelectionFilter.value ===
        UserSelectionQuickFilter.UNSELECTED_USERS
    ) {
      users = results.value.results.filter(
        (user: User) => !initialSelectedUsers.value?.includes(user.login),
      );
    } else {
      users = results.value.results;
    }
    return users;
  }
});

async function reloadWithContext() {
  await fetchData(
    results.value?.pagination.page,
    results.value?.pagination.pageSize,
  );
  await setSelection();
}

async function reloadData(page?: number, pageSize?: number) {
  await fetchData(page, pageSize);
  await setSelection();
}

async function onClickUser(user: User) {
  if (can(ACLRoles.CAN_WRITE_USER) && !props.disableClickable) {
    await navigateToPath(`${props.pathContext}/${user.login}`);
  }
}

function onSelectUsers(users: User[]) {
  const computeSelectedUsers = selectedUsers.value
    ?.filter(
      (selectedUser: string) =>
        !tableRef.value ||
        !(tableRef.value.data as User[])?.find(
          (row: User) => row.login === selectedUser,
        ),
    )
    .filter(
      (selectedUser: string) =>
        !users.find((row) => row.login === selectedUser),
    )
    .concat(users.map((user) => user.login));
  selectedUsers.value = computeSelectedUsers;
}

async function setSelection() {
  if (selectedUsers.value && tableRef?.value && tableRef.value.data) {
    tableRef.value.toggleTableSelectionMode(selectedUsers.value.length > 0);
    for (const row of tableRef.value.data) {
      tableRef.value.toggleRowSelection(
        row,
        selectedUsers.value?.some(
          (selectedUser) => selectedUser === (row as User).login,
        ),
      );
    }
  }
}

function onCloseCanvas(event: CloseEvent) {
  onClose(event, props.pathContext, reloadData);
  currentSelectedUser.value = undefined;
}

async function filterBySelection(selectionFilter: UserSelectionQuickFilter) {
  initialSelectedUsers.value = selectedUsers.value?.slice(0) ?? [];
  selectedUsersSelectionFilter.value = selectionFilter;
  await reloadData();
}

function handleFilterClick({
  group,
  item,
}: {
  group: QuickFilterGroup;
  item: QuickFilterItem;
}) {
  if (group.handler) {
    group.handler(item);
  }
}

watch(() => selectedUsers.value, setSelection);

onSearch(reloadData);
</script>

<template>
  <div class="mb-4 w-1/2">
    <search-bar
      v-model:search-query="searchQuery"
      :search-placeholder="t('filters.searchByLastNameOrFirstName')"
      @clear="search"
      @search="search"
    />
  </div>

  <list-quick-filters
    v-if="showQuickFilterSelectedUsers"
    :groups="quickFilterGroups"
    class="flex flex-wrap gap-2 mb-6"
    @filter-click="handleFilterClick"
  />

  <lxc-table
    ref="tableRef"
    min-width="68rem"
    :is-loading="isLoading || isLoadingGetCurrentUser"
    :data="computedUsers"
    :empty-text="t('user.empty')"
    :context="results?.pagination"
    :error="error?.toError() || errorGetCurrentUser?.toError()"
    :clickable="can(ACLRoles.CAN_WRITE_USER)"
    @change-page-and-page-size="reloadData"
    @sort-change="onSortChange"
    @row-click="onClickUser"
    @select="onSelectUsers"
  >
    <lxc-table-column v-if="selectedUsers" type="selection" />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.LAST_NAME)"
      prop="lastName"
      :label="t('user.table.column.lastName')"
      min-width="10rem"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.FIRST_NAME)"
      prop="firstName"
      :label="t('user.table.column.firstName')"
      min-width="10rem"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.PROFILE)"
      :label="t('user.table.column.profile')"
      class="!py-0"
      width="10rem"
    >
      <template #default="scope">
        <badge-for-list
          :label="getProfileLabel(scope.row.profile)"
          type="secondary"
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.EMAIL)"
      prop="email"
      :label="t('user.table.column.email')"
      width="15rem"
    />
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.GROUPS)"
      :label="t('user.table.column.groups')"
      class="!py-0"
      width="15rem"
    >
      <template #default="scope">
        <badge-for-list
          :label="getGroupsLabel(scope.row.groups)"
          type="secondary"
        />
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="!columns || columns.includes(UserColumns.STATUS)"
      :label="t('user.table.column.status')"
      width="8rem"
    >
      <template #default="scope">
        {{
          scope.row.active ? t("user.status.active") : t("user.status.inactive")
        }}
      </template>
    </lxc-table-column>
    <lxc-table-column
      v-if="
        can(ACLRoles.CAN_WRITE_USER) &&
        !useExternalIdp &&
        (!columns || columns.includes(UserColumns.ACTIONS))
      "
      class="!py-0 !px-0"
      width="6rem"
    >
      <template #default="scope">
        <div class="flex gap-4">
          <user-list-actions
            :user="scope.row"
            :action="UserActions.ACTIVATE_DEACTIVATE"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            :current-user="currentUser"
            :all-profiles="allProfiles"
            @change="reloadWithContext"
          />
          <user-list-actions
            :user="scope.row"
            :action="UserActions.DELETE"
            :business-unit-code="businessUnitCode"
            :organization-code="organizationCode"
            :current-user="currentUser"
            :all-profiles="allProfiles"
            @change="reloadWithContext"
          />
        </div>
      </template>
    </lxc-table-column>
  </lxc-table>

  <user-form
    ref="userFormRef"
    v-model:show="showUserForm"
    :business-unit-code="businessUnitCode"
    :organization-code="organizationCode"
    :organization-type="organizationType"
    :user-login="currentSelectedUser"
    :use-external-idp="useExternalIdp"
    @close="onCloseCanvas"
  ></user-form>
</template>

<style lang="scss" scoped>
:deep(table) {
  tbody {
    tr {
      &:hover {
        button {
          visibility: visible;
        }
      }
    }
  }
}
</style>
